import React from 'react'
import {
  Box,
  Button,
  Chip,
  Typography,
  Stack,
  Paper,
} from '@mui/material'
import TransporterGrid, { LogisticsUserGridRow } from '../views/UsersGrid'
import { TransporterWithPackages } from '@otw/models'
import { TransporterDriverUserRequest, TransporterGroup } from '@otw/models/transporter'
import { SearchHeader } from '../common'

interface UsersTabProps {
  transportersWithPackages: LogisticsUserGridRow[]
  onEditTransporter: (transporter: TransporterWithPackages) => void
  onDeleteTransporter: (userId: string) => void
  onCreateTransporter: () => void
  onCreateUser: () => void
  onEditUser?: (transporter: TransporterWithPackages) => void
  transporterGroups: TransporterGroup[]
  pendingDriverUserRequests: TransporterDriverUserRequest[]
  onApproveDriverUserRequest: (requestId: string) => Promise<void>
  onRejectDriverUserRequest: (requestId: string) => Promise<void>
  onViewDriverUserRequestLicense: (request: TransporterDriverUserRequest) => Promise<void>
  onViewTransporterLicense: (transporter: TransporterWithPackages) => Promise<void>
  processingRequestId?: string | null
}

const UsersTab: React.FC<UsersTabProps> = ({
  transportersWithPackages,
  onEditTransporter,
  onDeleteTransporter,
  onCreateTransporter,
  onCreateUser,
  onEditUser,
  transporterGroups,
  pendingDriverUserRequests,
  onApproveDriverUserRequest,
  onRejectDriverUserRequest,
  onViewDriverUserRequestLicense,
  onViewTransporterLicense,
  processingRequestId,
}) => {
  const handleDashboardActionClick = (actionType: 'photos' | 'locations' | 'transporters' | 'draft') => {
    // Handle dashboard action clicks
    switch (actionType) {
      case 'draft':
        console.log('Navigate to draft packages view')
        break
      case 'photos':
        console.log('Navigate to photos view')
        break
      case 'locations':
        console.log('Navigate to locations view')
        break
      case 'transporters':
        console.log('Navigate to transporters view')
        break
    }
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {pendingDriverUserRequests.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6">Pending driver requests</Typography>
            <Chip label={`${pendingDriverUserRequests.length} pending`} color="warning" size="small" />
          </Box>
          <Stack spacing={1}>
            {pendingDriverUserRequests.map((request) => {
              const companyName =
                transporterGroups.find((group) => group.id === request.company_id)?.name ?? request.company_id
              const isProcessing = processingRequestId === request.id
              return (
                <Paper key={request.id} variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {request.first_name} {request.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {request.email} · {request.phone}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Company: {companyName}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={isProcessing || !request.driver_license_front_storage_path}
                        onClick={() => onViewDriverUserRequestLicense(request)}
                        sx={{ textTransform: 'none' }}
                      >
                        View license
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isProcessing}
                        onClick={() => onApproveDriverUserRequest(request.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        {isProcessing ? 'Processing...' : 'Approve'}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={isProcessing}
                        onClick={() => onRejectDriverUserRequest(request.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              )
            })}
          </Stack>
        </Paper>
      )}

      <SearchHeader
        searchTerm=""
        onSearchChange={() => {}}
        searchPlaceholder=""
        showSearch={false}
        showSecondaryButton={false}
        showCreateButton={true}
        createButtonText="New User"
        onCreateClick={onCreateUser}
        showDashboard={false}
      />

      {transportersWithPackages.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body1' color='text.secondary'>
            No users found. Add a user to get started.
          </Typography>
        </Paper>
      ) : (
        <TransporterGrid
          transporters={transportersWithPackages}
          onEditTransporter={onEditTransporter}
          onDeleteTransporter={onDeleteTransporter}
          onEditUser={onEditUser}
          onViewTransporterLicense={onViewTransporterLicense}
        />
      )}
    </Stack>
  )
}

export default UsersTab
